import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Label, TableItem } from 'components/atoms';
import TotalCard from 'components/molecules/card/total/TotalCard';
import { Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useCountdown } from 'hooks';
import toast from 'react-hot-toast';
import { PiArrowsClockwise, PiCheck, PiWarning } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import {
  useGetBackgroundServiceHealth,
  useGetDigitalDeliveryErrorsCount,
  useGetPendingWorkCount,
} from 'services/background-service-health';
import { IBackgroundServiceHealth } from 'services/background-service-health/types';
import { DELIVERY_STATUS } from 'services/music-product-digital/types/enums';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';

const BACKGROUND_SERVICE_STATUS_OK = 0;

const sortBackgroundServices = (
  a: IBackgroundServiceHealth,
  b: IBackgroundServiceHealth,
) => {
  const aIsOk = a.status === BACKGROUND_SERVICE_STATUS_OK;
  const bIsOk = b.status === BACKGROUND_SERVICE_STATUS_OK;

  if (aIsOk !== bIsOk) {
    return aIsOk ? 1 : -1;
  }

  if (a.status !== b.status) {
    return b.status - a.status;
  }

  return a.serviceName.localeCompare(b.serviceName);
};

const Logs = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pendingWorkCount = useGetPendingWorkCount();
  const backgroundServices = useGetBackgroundServiceHealth();
  const digitalDeliveryErrorsCount = useGetDigitalDeliveryErrorsCount();

  const onClickToCopy = (title: string, value: string) => {
    toast.success(`${title} ${value} has been copied to clipboard!`);
    navigator.clipboard.writeText(value);
  };

  const countProps = (count?: number, variant?: BUTTON_VARIANT) =>
    !!count
      ? { variant, value: count }
      : { variant: BUTTON_VARIANT.default, value: '-' };

  const onGoToDigitalDeliveryStatusErrors = (status: DELIVERY_STATUS) =>
    navigate(`digital-delivery-errors?statusId=${status}`);

  const { startCounter, isCountingDown, countdown } = useCountdown(10, false);

  const backgroundServiceNodes = useMemo(
    () =>
      backgroundServices.data
        ?.slice()
        .sort(sortBackgroundServices)
        .map(b => ({ ...b, id: b.serviceName })) ?? [],
    [backgroundServices.data],
  );

  const onRefetchLogs = async () => {
    startCounter();
    await pendingWorkCount.refetch();
    await backgroundServices.refetch();
    await digitalDeliveryErrorsCount.refetch();
    toast.success('Logs refreshed');
  };

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={`Logs`}
          hideBackButton
          topbarContent={
            <Button
              onlyIcon
              variant={BUTTON_VARIANT.submit}
              onClick={onRefetchLogs}
              disabled={isCountingDown}>
              <Button.Description description={t('button.refresh')} />

              <Button.Icon icon={<PiArrowsClockwise />} />
              {isCountingDown && <Button.Text text={`${countdown}s`} />}
            </Button>
          }
        />
      }>
      <div className='flex flex-col w-full gap-4 mb-4'>
        <div className='flex w-full gap-4'>
          <TotalCard
            label={'Tracks waiting to convert'}
            {...countProps(
              pendingWorkCount.data?.tracksToConvert,
              BUTTON_VARIANT.submit,
            )}
          />
          <TotalCard
            label={'Videos waiting to convert'}
            {...countProps(
              pendingWorkCount.data?.videosToConvert,
              BUTTON_VARIANT.submit,
            )}
          />
          <TotalCard
            label={'Reports waiting or in progress'}
            {...countProps(
              pendingWorkCount.data?.reportsWaitingOrInProgress,
              BUTTON_VARIANT.submit,
            )}
          />
          <TotalCard
            label={'Settlements waiting or in progress'}
            {...countProps(
              pendingWorkCount.data?.settlementsWaitingOrInProgress,
              BUTTON_VARIANT.submit,
            )}
          />
        </div>
        <div className='flex w-full gap-4'>
          <TotalCard
            label={'Delivery errors'}
            {...countProps(
              digitalDeliveryErrorsCount.data?.errorCount,
              BUTTON_VARIANT.remove,
            )}
            onClick={() =>
              onGoToDigitalDeliveryStatusErrors(DELIVERY_STATUS.error)
            }
          />
          <TotalCard
            label={'Delivery ACK File errors'}
            {...countProps(
              digitalDeliveryErrorsCount.data?.ackFileError,
              BUTTON_VARIANT.remove,
            )}
            onClick={() =>
              onGoToDigitalDeliveryStatusErrors(DELIVERY_STATUS.ACKFileError)
            }
          />
          <TotalCard
            label={'Delivery ACK File processing errors'}
            {...countProps(
              digitalDeliveryErrorsCount.data?.errorProcessingACKFile,
              BUTTON_VARIANT.remove,
            )}
            onClick={() =>
              onGoToDigitalDeliveryStatusErrors(
                DELIVERY_STATUS.errorProcessingACKFile,
              )
            }
          />
          <TotalCard
            label={'Delivery assigning AssetIds errors'}
            {...countProps(
              digitalDeliveryErrorsCount.data?.errorAssigningAssetIds,
              BUTTON_VARIANT.remove,
            )}
            onClick={() =>
              onGoToDigitalDeliveryStatusErrors(
                DELIVERY_STATUS.errorAssigningAssetIds,
              )
            }
          />
          <TotalCard
            label={'Delivery withdraw error'}
            {...countProps(
              digitalDeliveryErrorsCount.data?.withdrawError,
              BUTTON_VARIANT.remove,
            )}
            onClick={() =>
              onGoToDigitalDeliveryStatusErrors(DELIVERY_STATUS.withdrawError)
            }
          />
        </div>
      </div>
      <Table
        loading={backgroundServices.isLoading}
        size='compact'
        // displayMode
        data={{
          nodes: backgroundServiceNodes,
        }}
        columns={[
          {
            label: 'Service name',
            renderCell: ({ serviceName, lastTickDateTime }) => (
              <TableItem
                className='cursor-pointer'
                onClick={() => onClickToCopy('Service name', serviceName)}>
                <TableItem.Text text={serviceName} />
                <TableItem.Text
                  isSupplementary
                  text={lastTickDateTime.split('T').join(' ')}
                />
              </TableItem>
            ),
            resize: true,
          },
          {
            label: 'Status',
            renderCell: ({ status }) => (
              <TableItem>
                {status === 0 && (
                  <Label color='green'>
                    <PiCheck /> OK
                  </Label>
                )}
                {status === 1 && (
                  <Label color='orange'>
                    <PiArrowsClockwise /> Not responding
                  </Label>
                )}
                {status === 2 && (
                  <Label color='red'>
                    <PiWarning /> Critical error
                  </Label>
                )}
              </TableItem>
            ),
            resize: true,
          },
        ]}
      />
    </TopbarPage>
  );
};

export default Logs;

// 0 - zdrowy
// 1- not responding
// 2 - critical error
